import { PiggyBank } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Skeleton } from "@/core/components/ui/skeleton";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSavingsSummary } from "@/features/savings/api/use-savings-summary";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/**
 * Cartes d'agrégat des coffres ouverts (`GET /SavingsVaults/summary`) :
 * nombre de coffres puis, par devise, solde total / part bloquée / part
 * retirable. Posées en haut de la page liste.
 */
export function SavingsSummaryCards() {
  const { t } = useTypedTranslation();
  const { data, isLoading } = useSavingsSummary();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-28" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("savings.summary.openVaultCount")}
          </CardTitle>
          <PiggyBank className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <NoTranslate as="span" className="block text-2xl font-semibold">
            {data.openVaultCount}
          </NoTranslate>
        </CardContent>
      </Card>

      {data.totals.map((total) => (
        <Card key={total.currency}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("savings.summary.totalBalance")}
            </CardTitle>
            <NoTranslate as="span">
              <span className="text-xs font-medium text-muted-foreground">
                {total.currency}
              </span>
            </NoTranslate>
          </CardHeader>
          <CardContent className="space-y-1">
            <AmountMinor
              amountMinor={total.totalBalanceMinor}
              currency={total.currency}
              className="block text-2xl font-semibold"
            />
            <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
              <span>
                {t("savings.summary.lockedBalance")} :{" "}
                <AmountMinor
                  amountMinor={total.lockedBalanceMinor}
                  currency={total.currency}
                />
              </span>
              <span>
                {t("savings.summary.withdrawableBalance")} :{" "}
                <AmountMinor
                  amountMinor={total.withdrawableBalanceMinor}
                  currency={total.currency}
                />
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
